import { Loader2, ShoppingCart } from "lucide-react";
import { Button } from "../ui/button";

const PRICING_TIERS = [
  {
    count: 1,
    price: 20,
    label: "1 Country Guide",
    sub: "Perfect for focused research",
    savings: 0,
    accent: "primary",
  },
  {
    count: 2,
    price: 30,
    label: "2 Country Guides",
    sub: null,
    savings: 10,
    accent: "secondary",
    badge: "POPULAR",
  },
  {
    count: 3,
    price: 35,
    label: "3 Country Guides",
    sub: null,
    savings: 25,
    accent: "accent",
    badge: "BEST VALUE",
  },
];

const getBundlePrice = (n) =>
  n === 1 ? 20 : n === 2 ? 30 : n >= 3 ? 35 : 0;
const getBundleSavings = (n) =>
  n === 1 ? 0 : n === 2 ? 10 : n >= 3 ? 25 : 0;

export const BundleCheckoutSection = ({
  selectedForBundle,
  walletCreditDollars,
  paymentMethod,
  setPaymentMethod,
  onCheckout,
  loading,
}) => {
  const count = selectedForBundle.length;
  const gross = getBundlePrice(count);
  const savings = getBundleSavings(count);
  const discount = Math.min(walletCreditDollars, Math.max(gross - 0.5, 0));
  const net = Math.max(gross - discount, 0.5);
  const hasCredit = walletCreditDollars > 0 && count > 0 && discount > 0;

  return (
    <div className="bg-gradient-to-r from-secondary/10 to-accent/10 rounded-2xl border border-secondary/20 p-8 mb-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-primary mb-2">
          {count > 0
            ? `Your Bundle: ${count} ${count === 1 ? "Country" : "Countries"} Selected`
            : "Save More with Bundle Pricing"}
        </h2>
        <p className="text-muted-foreground">
          {count > 0
            ? `Selected: ${selectedForBundle.join(", ")}`
            : "Compare your top matches side-by-side and make a confident decision"}
        </p>
      </div>

      {count > 0 && (
        <div className="bg-white/80 rounded-xl p-6 mb-6 border-2 border-accent">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              {hasCredit ? (
                <>
                  <p className="text-sm text-muted-foreground line-through">${gross}</p>
                  <p className="text-3xl font-bold text-accent">${net.toFixed(2)}</p>
                  <p
                    className="text-sm text-secondary font-medium"
                    data-testid="credit-applied-label"
                  >
                    ${discount.toFixed(0)} referral credit applied
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-accent">${gross}</p>
                  {savings > 0 && (
                    <p className="text-sm text-accent font-medium">You save ${savings}!</p>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Pay with:</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-primary/20 bg-white text-sm"
                  data-testid="payment-method-select"
                >
                  <option value="stripe">Credit Card (Stripe)</option>
                  <option value="paypal">PayPal</option>
                </select>
              </div>

              <Button
                onClick={onCheckout}
                disabled={loading}
                className="btn-animated bg-accent hover:bg-accent/90 text-white gap-2 px-8"
                data-testid="bundle-checkout-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-4 w-4" />
                    Checkout Bundle
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {PRICING_TIERS.map((tier) => {
          const highlight =
            count === tier.count || (tier.count === 3 && count >= 3);
          const cardBorder = highlight
            ? `border-2 border-${tier.accent}`
            : tier.badge
            ? `border-2 border-${tier.accent}/50`
            : "border-primary/10";
          return (
            <div
              key={tier.count}
              className={`bg-card rounded-xl p-6 text-center relative border ${cardBorder}`}
              data-testid={`pricing-tier-${tier.count}`}
            >
              {tier.badge && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 bg-${tier.accent} text-white text-xs px-3 py-1 rounded-full font-medium`}
                >
                  {tier.badge}
                </div>
              )}
              <p className={`text-3xl font-bold text-${tier.accent}`}>
                ${tier.price}
              </p>
              <p className="text-sm text-muted-foreground mb-2">{tier.label}</p>
              {tier.savings > 0 ? (
                <p className="text-xs text-accent font-medium">Save ${tier.savings}!</p>
              ) : (
                <p className="text-xs text-muted-foreground">{tier.sub}</p>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-center text-sm text-muted-foreground mt-4">
        💡 <strong>Pro tip:</strong> Most successful expats compare at least 2-3 countries
        before deciding. Get the full picture and avoid costly surprises.
      </p>
    </div>
  );
};

export { getBundlePrice, getBundleSavings };
export default BundleCheckoutSection;
